<template>
  <div class="relative w-full">
    <RevRadio
      :id
      :aria-describedby="`${id}-description`"
      :aria-labelledby="`${id}-label`"
      :class="[
        $style.radio,
        {
          [$style.blueHighlighted]: isHighlighted,
        },
      ]"
      :model-value="isSelected"
      :tagProps
      :value="id"
      variant="full"
      @click="handleClick"
    >
      <template #label>
        <div :id="`${id}-label`" class="space-y-8">
          <span v-if="tagProps" class="sr-only">{{ tagProps.label }}</span>

          <span class="body-1-bold">{{ offer.title }}</span>

          <template v-if="offer.price && offer.price.amount !== '0.00'">
            <InsurancePrice
              :payment-frequency="offer.paymentFrequency"
              :price="offer.price"
            />
          </template>
        </div>
      </template>
      <template #description>
        <RevDivider
          class="relative -inset-x-[38px] mb-16 mt-8 w-[calc(100%+38px)]"
        />
        <ul
          :id="`${id}-description`"
          class="relative -inset-x-8 mt-8 space-y-8"
        >
          <li
            v-for="benefit in offer.benefits.short"
            :key="benefit"
            class="flex items-center"
          >
            <IconCheckSmall class="shrink-0 self-start" /> {{ benefit }}
          </li>
        </ul>
      </template>
    </RevRadio>
  </div>
</template>

<script setup lang="ts">
import type { InsuranceOffer } from '@backmarket/http-api/src/api-models/InsuranceOffer'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevDivider } from '@ds/components/Divider'
import { RevRadio } from '@ds/components/Radio'
import { VariantsEnum } from '@ds/components/Tag/Tag.constant'
import { IconCheckSmall } from '@ds/icons/IconCheckSmall'

import InsurancePrice from '../InsurancePrice/InsurancePrice.vue'

const { trackClick } = useTracking()

const props = defineProps<{
  id: string
  offer: InsuranceOffer
  isSelected: boolean
  isHighlighted: boolean
  trackingZone: string
  tagProps: { label: string; variant: VariantsEnum } | undefined
}>()

const emit = defineEmits(['select', 'remove'])

function handleClick() {
  trackClick({
    name: props.offer.title,
    value: props.offer.paymentFrequency,
    zone: props.trackingZone,
  })

  emit(props.isSelected ? 'remove' : 'select')
}
</script>

<style lang="css" module>
.radio {
  @apply h-full;
}

.radio > label {
  @apply h-full;
  @apply border-l-6;
  @apply border-l-static-default-mid;
}

.radio.blueHighlighted > label {
  @apply border-l-action-brand-hi;
  @apply hover:bg-static-info-low;
}
</style>
